import { EChart as ReactEChartsCore } from './EChart';
import * as echarts from 'echarts/core';
import { PieChart as EChartsPie } from 'echarts/charts';
import { AriaComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { SHARE_BY_PROJECT } from '../../data/fixtures';
import { formatMoney, formatPercentage } from '../../i18n/format';
import { ChartFrame } from './ChartFrame';
import { ChartLegend, type ChartLegendItem } from './ChartLegend';
import { entranceAnimation, seriesColor, tooltipTokens, useChartMotion, useChartTheme } from './ChartTheme';

echarts.use([EChartsPie, AriaComponent, TooltipComponent, CanvasRenderer]);

const money = (value: number) => formatMoney(value, { precision: 0 });
const shareOfTotal = (value: number, total: number) =>
  formatPercentage((value / total) * 100, { precision: 1 });

// Module scope, so the option stays deep-equal across unrelated re-renders
// (echarts-for-react deep-compares it with fast-deep-equal, lib/core.js:52).
function sliceLabel(params: { name: string; percent: number }): string {
  return `${params.name} ${formatPercentage(params.percent, { precision: 1 })}`;
}

export function PieChart() {
  const theme = useChartTheme();
  const motion = useChartMotion(theme);

  const total = SHARE_BY_PROJECT.reduce((sum, row) => sum + row.value, 0);

  // A descriptive legend, deliberately: filtering a pie would rescale every
  // remaining slice, so `params.percent` would stop meaning "share of the
  // portfolio" and would contradict this chart's own data table. The other
  // charts in the family carry the interactive legend.
  const legendItems: ChartLegendItem[] = SHARE_BY_PROJECT.map((row, index) => ({
    id: row.name,
    label: row.name,
    color: seriesColor(theme, index),
    value: money(row.value),
  }));

  const data = SHARE_BY_PROJECT.map((row, index) => ({
    name: row.name,
    value: row.value,
    itemStyle: { color: seriesColor(theme, index) },
  }));

  const largest = SHARE_BY_PROJECT.reduce(
    (best, row) => (row.value > best.value ? row : best),
    SHARE_BY_PROJECT[0],
  );

  return (
    <ChartFrame
      title="Capital by project"
      description={`Contributed capital across ${SHARE_BY_PROJECT.length} holdings.`}
      summary={`Capital contributed across ${SHARE_BY_PROJECT.length} projects, totalling ${money(total)}. ${
        largest.name
      } is the largest holding, at ${shareOfTotal(largest.value, total)} of the portfolio.`}
      chartLabel={`Pie chart of contributed capital across ${SHARE_BY_PROJECT.length} projects`}
      legend={<ChartLegend label="Projects" items={legendItems} />}
      table={{
        caption: 'Contributed capital by project',
        columns: ['Project', 'Contributed', 'Share of portfolio'],
        rows: SHARE_BY_PROJECT.map((row) => [
          row.name,
          money(row.value),
          shareOfTotal(row.value, total),
        ]),
      }}
    >
      <ReactEChartsCore
        echarts={echarts}
        style={{ width: '100%', height: '100%' }}
        option={{
          ...entranceAnimation(motion),
          aria: { enabled: true, label: { enabled: false }, decal: { show: true } },
          tooltip: { trigger: 'item', ...tooltipTokens(theme, money) },
          series: [
            {
              id: 'capital',
              type: 'pie',
              radius: ['46%', '74%'],
              center: ['50%', '50%'],
              minAngle: 4,
              avoidLabelOverlap: true,
              // The slice boundary is the surface, so the ring reads as separate
              // segments in both themes without a rule drawn on each slice.
              itemStyle: { borderColor: theme.surface, borderWidth: 2 },
              label: {
                color: theme.textHeading,
                fontFamily: theme.fontFamily,
                fontSize: theme.fontSizeXs,
                formatter: sliceLabel,
              },
              labelLine: { lineStyle: { color: theme.border } },
              data,
            },
          ],
        }}
      />
    </ChartFrame>
  );
}
